import { getStroke } from "perfect-freehand";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { S } from "../strings";

type Point = [number, number, number];
type Stroke = { points: Point[]; pen: boolean };

const INK = "#1b2a6b";

function strokeOptions(pen: boolean, last: boolean) {
  return { size: 3.6, thinning: 0.62, smoothing: 0.55, streamline: 0.45, simulatePressure: !pen, last, start: { taper: 6, cap: true }, end: { taper: 10, cap: true } };
}

/** An outline from perfect-freehand → an SVG path, with quadratic curves through the midpoints. */
function outlinePath(outline: number[][]): string {
  if (outline.length < 2) return "";
  const first = outline[0]!;
  const parts: (string | number)[] = ["M", first[0]!, first[1]!, "Q"];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    parts.push(a[0]!, a[1]!, (a[0]! + b[0]!) / 2, (a[1]! + b[1]!) / 2);
  }
  parts.push("Z");
  return parts.map((p) => (typeof p === "number" ? p.toFixed(2) : p)).join(" ");
}

function paint(ctx: CanvasRenderingContext2D, strokes: Stroke[], scale: number, dx = 0, dy = 0) {
  ctx.save();
  ctx.setTransform(scale, 0, 0, scale, -dx * scale, -dy * scale);
  ctx.fillStyle = INK;
  for (const s of strokes) {
    const d = outlinePath(getStroke(s.points, strokeOptions(s.pen, true)));
    if (d && typeof Path2D !== "undefined") ctx.fill(new Path2D(d));
  }
  ctx.restore();
}

function bounds(strokes: Stroke[]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, n = 0;
  for (const s of strokes)
    for (const [x, y] of s.points) {
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); n++;
    }
  return { x0, y0, x1, y1, w: x1 - x0, h: y1 - y0, n };
}

/** Too small, too few points, or a flat line isn't a signature. */
export function isTooSmall(strokes: Stroke[], padWidth: number): boolean {
  const b = bounds(strokes);
  if (b.n < 10) return true;
  return b.w < Math.max(60, padWidth * 0.18) || b.h < 14;
}

/** A drawing pad with smooth, pressure-aware strokes. Gives back a cropped PNG, or null while it isn't a signature. */
export function SignaturePad({ onChange }: { onChange: (png: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const live = useRef<Stroke | null>(null);
  const frame = useRef(0);
  const [tooSmall, setTooSmall] = useState(false);
  const size = useRef({ w: 0, h: 0, dpr: 1 });
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;

  const redraw = useCallback((list: Stroke[]) => {
    const c = canvas.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    paint(ctx, live.current ? [...list, live.current] : list, size.current.dpr);
  }, []);

  useLayoutEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const fit = () => {
      const r = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      size.current = { w: r.width, h: r.height, dpr };
      c.width = Math.max(1, Math.round(r.width * dpr));
      c.height = Math.max(1, Math.round(r.height * dpr));
      redraw(strokesRef.current);
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
  }, [redraw]);

  useEffect(() => redraw(strokes), [strokes, redraw]);

  const emit = (list: Stroke[]) => {
    if (list.length === 0) {
      setTooSmall(false);
      onChange(null);
      return;
    }
    if (isTooSmall(list, size.current.w)) {
      setTooSmall(true);
      onChange(null);
      return;
    }
    setTooSmall(false);
    const b = bounds(list);
    const pad = 12;
    const scale = 2;
    const out = document.createElement("canvas");
    out.width = Math.ceil((b.w + pad * 2) * scale);
    out.height = Math.ceil((b.h + pad * 2) * scale);
    const ctx = out.getContext("2d");
    if (!ctx) {
      onChange(null);
      return;
    }
    paint(ctx, list, scale, b.x0 - pad, b.y0 - pad);
    onChange(out.toDataURL("image/png"));
  };

  const at = (e: { clientX: number; clientY: number; pressure: number; pointerType: string }): Point => {
    const r = canvas.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top, e.pointerType === "pen" ? e.pressure || 0.5 : 0.5];
  };

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    live.current = { points: [at(e)], pen: e.pointerType === "pen" };
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const s = live.current;
    if (!s) return;
    const evs = typeof e.nativeEvent.getCoalescedEvents === "function" ? e.nativeEvent.getCoalescedEvents() : [];
    for (const ev of evs.length ? evs : [e.nativeEvent]) s.points.push(at(ev));
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => redraw(strokesRef.current));
  };
  const up = () => {
    const s = live.current;
    if (!s) return;
    live.current = null;
    const next = [...strokesRef.current, s];
    setStrokes(next);
    emit(next);
  };

  const undo = () => {
    const next = strokes.slice(0, -1);
    setStrokes(next);
    emit(next);
  };
  const clear = () => {
    setStrokes([]);
    emit([]);
  };

  return (
    <div className="stack gap-8">
      <div className="pad">
        <canvas
          ref={canvas}
          role="img"
          aria-label={S.adopt.drawLabel}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onLostPointerCapture={up}
        />
        <span className="pad-x" aria-hidden="true">
          ×
        </span>
        <span className="pad-line" aria-hidden="true" />
        {strokes.length === 0 && (
          <span className="pad-hint" aria-hidden="true">
            {S.adopt.drawHint}
          </span>
        )}
      </div>
      <div className="row between">
        <span className={tooSmall ? "err small-err" : "help"} aria-live="polite">
          {tooSmall ? (
            <>
              <Icon n="alert" /> {S.adopt.tooSmall}
            </>
          ) : (
            S.adopt.drawHelp
          )}
        </span>
        <span className="row gap-4">
          <button type="button" className="btn-link" onClick={undo} disabled={strokes.length === 0}>
            <Icon n="undo" size={15} /> {S.adopt.undo}
          </button>
          <button type="button" className="btn-link" onClick={clear} disabled={strokes.length === 0}>
            {S.adopt.clear}
          </button>
        </span>
      </div>
    </div>
  );
}
